import { DefaultChatTransport, isToolUIPart, type ToolUIPart, type UIMessage } from 'ai';
import type { ToolDefinition } from './parent-bridge';

export type AgentMeta = { name: string; description: string };

export type SessionResponse = {
  sessionToken: string;
  expiresIn: number;
  agent: AgentMeta;
};

export class HandshakeError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = 'HandshakeError';
  }
}

const WIDGET_API_URL = import.meta.env.VITE_WIDGET_API_URL ?? '';

if (!WIDGET_API_URL) {
  // Dev-time fail-fast — the build is unusable without this env var.
  // Don't throw (it'd break SSR / unit tests); surface via console so the
  // empty fetch attempt doesn't silently 404.
  console.warn('[agents embed] VITE_WIDGET_API_URL is empty; API calls will fail');
}

export type EndUserInfo = {
  id: string;
  name?: string;
  email?: string;
};

/**
 * Plano-15 IV — assinatura do bundle de identidade. Top-level (sibling de
 * endUser/customerMetadata) pra cobrir id E/OU metadata, inclusive sessão
 * só-metadata sem endUser.
 */
export type IdentitySignature = { exp: number; signature: string };

export async function postSession(opts: {
  publicKey: string;
  parentOrigin: string;
  endUser?: EndUserInfo;
  customerMetadata?: Record<string, string>;
  identitySignature?: IdentitySignature;
}): Promise<SessionResponse> {
  let res: Response;
  try {
    res = await fetch(`${WIDGET_API_URL}/widget/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        publicKey: opts.publicKey,
        parentOrigin: opts.parentOrigin,
        ...(opts.endUser ? { endUser: opts.endUser } : {}),
        ...(opts.customerMetadata ? { customerMetadata: opts.customerMetadata } : {}),
        ...(opts.identitySignature
          ? { exp: opts.identitySignature.exp, signature: opts.identitySignature.signature }
          : {}),
      }),
    });
  } catch (err) {
    throw new HandshakeError(0, 'NETWORK', (err as Error).message ?? 'network error');
  }

  let body: {
    sessionToken?: string;
    expiresIn?: number;
    agent?: AgentMeta;
    error?: string;
  } = {};
  try {
    body = await res.json();
  } catch {
    /* non-JSON 5xx */
  }

  if (!res.ok || !body.sessionToken || !body.expiresIn || !body.agent) {
    throw new HandshakeError(res.status, body.error ?? 'unknown', body.error ?? res.statusText);
  }

  return {
    sessionToken: body.sessionToken,
    expiresIn: body.expiresIn,
    agent: body.agent,
  };
}

/**
 * Build a chat transport that:
 *   - pins `Authorization: Bearer <sessionToken>` per request
 *   - on 401 mid-stream throws `HandshakeError(401, 'session_expired')`
 *     (caller clears the session and re-handshakes — JWT TTL is 15min)
 *   - alternates between two delta-only body shapes per `messages[]` inspection:
 *       Shape A `{ turnId, userMessage, parentOrigin }` — fresh user turn
 *       Shape B `{ turnId, toolResults, parentOrigin }` — continuation of an
 *       open turn whose paused tool-calls now have outputs from the iframe
 *
 * The shape gate works by tracking `flushedToolCallIds` in the closure: when a
 * tool's `output-available` part appears AND we haven't sent that toolCallId
 * before, this resubmit is a Shape B. Otherwise it's a Shape A.
 *
 * `getCurrentTurnId` is the caller's responsibility — ChatPanel generates a
 * fresh turnId on every user submit and holds it stable through the
 * tool-result loop. Without this, Shape A would generate a new turnId mid-
 * loop and the server would 409 (no_open_turn).
 */
export type StoredMessagesResponse = {
  items: UIMessage[];
  assistantMode: 'agent' | 'human';
};

/**
 * Lê a janela recente de mensagens da própria thread (GET /widget/messages).
 * Usado pra carregar histórico ao reabrir (continuidade) e pra polling da
 * resposta humana enquanto em modo human. O backend já fez o strip (sem
 * reasoning / autoria humana / tool internos). 401 = sessão expirada.
 */
export async function fetchMessages(token: string): Promise<StoredMessagesResponse> {
  const res = await fetch(`${WIDGET_API_URL}/widget/messages`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) {
    throw new HandshakeError(401, 'session_expired', 'session expired');
  }
  if (!res.ok) {
    throw new HandshakeError(res.status, 'messages_failed', res.statusText);
  }
  return (await res.json()) as StoredMessagesResponse;
}

export function buildChatTransport(deps: {
  sessionToken: string;
  parentOrigin: string;
  getCurrentTurnId: () => string | null;
  /** Latest live context fornecido pelo host (página atual, filtros, etc.).
   *  Lido a cada send; null/undefined/empty = não envia o campo. */
  getLiveContext?: () => string | null | undefined;
  /** Definições de tools declaradas pelo parent (loader → init payload). Lido
   *  a cada send (re-derivado do estado do iframe — host pode mudar entre
   *  turns; backend valida). Empty array / undefined = não envia o campo. */
  getTools?: () => ToolDefinition[] | undefined;
}): DefaultChatTransport<UIMessage> {
  const flushedToolCallIds = new Set<string>();

  return new DefaultChatTransport<UIMessage>({
    api: `${WIDGET_API_URL}/widget/chat`,
    fetch: async (input, init) => {
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${deps.sessionToken}`);
      headers.set('Content-Type', 'application/json');
      const res = await fetch(input as string, { ...(init ?? {}), headers });
      if (res.status === 401) {
        throw new HandshakeError(401, 'session_expired', 'session expired');
      }
      if (!res.ok) {
        let code = 'ERROR';
        let message = res.statusText;
        try {
          const body = (await res.clone().json()) as { error?: string; message?: string };
          code = body.error ?? code;
          message = body.message ?? message;
        } catch {
          /* non-JSON */
        }
        throw new HandshakeError(res.status, code, message);
      }
      return res;
    },
    prepareSendMessagesRequest: ({ messages, messageId, trigger }) => {
      if (trigger === 'regenerate-message') {
        // Embed UI doesn't expose regenerate; backend doesn't support it.
        throw new Error('regenerate not supported');
      }

      const turnId = deps.getCurrentTurnId();
      if (!turnId) {
        throw new Error('no current turnId — caller must set it before sendMessage');
      }

      // Shape B: last assistant message has ≥1 tool part with output-available
      // whose toolCallId we haven't flushed yet (so this resubmit was triggered
      // by `sendAutomaticallyWhen` after addToolOutput).
      const last = messages[messages.length - 1];
      const pending: Array<ToolUIPart & { state: 'output-available' }> =
        last?.role === 'assistant'
          ? last.parts.filter(
              (p): p is ToolUIPart & { state: 'output-available' } =>
                isToolUIPart(p) &&
                p.state === 'output-available' &&
                !flushedToolCallIds.has(p.toolCallId),
            )
          : [];

      // Read latest live context per-send. Host updates it via the loader's
      // public API (RuneAgentsWidget.setLiveContext) which posts an update message
      // to this iframe; we cache the latest value and read it here. Empty/null
      // = don't send the field (backend treats as "no context block").
      const liveContextRaw = deps.getLiveContext?.();
      const liveContext = liveContextRaw && liveContextRaw.length > 0 ? liveContextRaw : undefined;

      const toolsRaw = deps.getTools?.();
      const tools = toolsRaw && toolsRaw.length > 0 ? toolsRaw : undefined;

      if (pending.length > 0) {
        const toolResults = pending.map((p) => ({
          toolCallId: p.toolCallId,
          output: p.output,
        }));
        for (const r of toolResults) flushedToolCallIds.add(r.toolCallId);
        return {
          body: {
            turnId,
            toolResults,
            parentOrigin: deps.parentOrigin,
            ...(liveContext ? { liveContext } : {}),
            ...(tools ? { tools } : {}),
          },
        };
      }

      // Shape A: fresh user turn. Reset bookkeeping (new turn zeroes flushed).
      flushedToolCallIds.clear();
      const target = messageId
        ? messages.find((m) => m.id === messageId)
        : [...messages].reverse().find((m) => m.role === 'user');
      if (!target || target.role !== 'user') {
        throw new Error('no user message to send');
      }
      return {
        body: {
          turnId,
          userMessage: { id: target.id, role: 'user', parts: target.parts },
          parentOrigin: deps.parentOrigin,
          ...(liveContext ? { liveContext } : {}),
          ...(tools ? { tools } : {}),
        },
      };
    },
  });
}
